<script setup lang="ts">
    import type { Company, SharedData } from '@/types';
    import PageCard from '@/components/PageCard.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/procedures';
    import { useQuery } from '@tanstack/vue-query';
    import axios from 'axios';
    import QueryKey from '@/enums/QueryKey';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import CreateProcedure from './CreateProcedure.vue';
    import { computed, watch } from 'vue';
    import { useDashboardStore } from '@/stores/dashboard';
    import { usePage } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import HoverInfo from '@/components/HoverInfo.vue';

    const props = defineProps<{
        company: Company;
    }>();

    const page = usePage<SharedData>();
    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    const dashboardStore = useDashboardStore();

    const companyId = computed(() => props.company.id);
    const isClient = computed(() => page.props.auth.user.is_client);

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.DASHBOARD_PROCEDURES, companyId],
        queryFn: getProcedures,
    });

    async function getProcedures() {
        const { data } = await axios.get(route('v2.dashboard.procedures.get'));
        return data.data;
    }

    watch(data, (newVal) => {
        if (newVal) {
            dashboardStore.setProcedureCount(newVal.length);
        }
    });
</script>

<template>
    <PageCard class="mt-0 overflow-hidden md:mt-1.5 md:ml-0">
        <DataTable
            :table-key="QueryKey.DASHBOARD_PROCEDURES"
            :columns="columns"
            :data="data"
            :loading="isPending"
            :default-column-visibility="{
                task_responsible_ids: false,
            }"
            :default-sorting="[
                {
                    id: 'procedure_deadline_text',
                    desc: false,
                },
            ]"
            :default-pagination="{
                pageIndex: 0,
                pageSize: 10,
            }"
            :enable-search="false"
            :enable-filters="false"
        >
            <template #title>
                <CardTitle class="flex items-center gap-2">
                    {{ $t('dashboards.procedures_header') }}
                    <HoverInfo
                        v-if="isClient"
                        :content="t('dashboards.procedures_help')"
                    />
                </CardTitle>
            </template>

            <template
                #actions
                v-if="hasPermission(Permission.DASHBOARD_ADMIN_CREATE)"
            >
                <CreateProcedure :company="company" />
            </template>
        </DataTable>
    </PageCard>
</template>
